<template>
  <div class="product-management">
    <el-card>
      <template #header>
        <div class="card-header">
          <span>商品管理</span>
          <div>
            <el-button type="success" @click="handleExport" :loading="exportLoading">导出商品</el-button>
            <el-button v-if="!isReadOnly" type="primary" @click="handleCreate">新增商品</el-button>
          </div>
        </div>
      </template>

      <!-- 搜索表单 -->
      <el-form :inline="true" :model="queryParams" class="search-form">
        <el-form-item label="关键词">
          <el-input
            v-model="queryParams.keyword"
            placeholder="商品名称/编码/规格"
            clearable
            @keyup.enter="handleSearch"
            style="width: 200px"
          />
        </el-form-item>
        <el-form-item label="所属仓库">
          <el-select
            v-model="queryParams.warehouseId"
            placeholder="请选择仓库"
            clearable
            style="width: 200px"
          >
            <el-option
              v-for="warehouse in warehouseList"
              :key="warehouse.id"
              :label="warehouse.warehouseName"
              :value="warehouse.id"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="启用状态">
          <el-select
            v-model="queryParams.status"
            placeholder="全部"
            clearable
            style="width: 200px"
          >
            <el-option label="启用" :value="1" />
            <el-option label="禁用" :value="0" />
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="handleSearch">查询</el-button>
          <el-button @click="handleReset">重置</el-button>
        </el-form-item>
      </el-form>

      <!-- 商品列表 -->
      <el-table
        v-loading="loading"
        :data="productList"
        border
        style="width: 100%"
      >
        <el-table-column prop="id" label="ID" width="80" />
        <el-table-column prop="productCode" label="商品编码" width="120" />
        <el-table-column prop="productName" label="商品名称" width="150" />
        <el-table-column prop="warehouseName" label="所属仓库" width="120" />
        <el-table-column prop="spec" label="规格" width="120" />
        <el-table-column prop="unit" label="单位" width="80" />
        <el-table-column prop="stock" label="库存" width="100" />
        <el-table-column prop="category" label="分类" width="120" />
        <el-table-column prop="costPrice" label="成本价" width="100">
          <template #default="{ row }">
            {{ row.costPrice ? '¥' + row.costPrice : '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="purchasePrice" label="采购价" width="100">
          <template #default="{ row }">
            {{ row.purchasePrice ? '¥' + row.purchasePrice : '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="salePrice" label="销售价" width="100">
          <template #default="{ row }">
            {{ row.salePrice ? '¥' + row.salePrice : '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="status" label="状态" width="80">
          <template #default="{ row }">
            <el-tag :type="row.status === 1 ? 'success' : 'danger'">
              {{ row.status === 1 ? '启用' : '禁用' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="remark" label="备注" />
        <el-table-column v-if="!isReadOnly" label="操作" width="200" fixed="right">
          <template #default="{ row }">
            <el-button type="primary" size="small" @click="handleEdit(row)">编辑</el-button>
            <el-button 
              :type="row.status === 1 ? 'danger' : 'success'" 
              size="small" 
              @click="handleToggleStatus(row)"
            >
              {{ row.status === 1 ? '禁用' : '启用' }}
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <div class="pagination">
        <el-pagination
          v-model:current-page="queryParams.page"
          v-model:page-size="queryParams.pageSize"
          :total="total"
          :page-sizes="[10, 20, 50, 100]"
          layout="total, sizes, prev, pager, next, jumper"
          @size-change="handleSizeChange"
          @current-change="handlePageChange"
        />
      </div>
    </el-card>

    <!-- 创建/编辑商品对话框 -->
    <el-dialog
      v-model="dialogVisible"
      :title="dialogTitle"
      width="600px"
      @close="handleDialogClose"
    >
      <el-form
        ref="productFormRef"
        :model="productForm"
        :rules="productFormRules"
        label-width="100px"
      >
        <el-form-item label="商品编码" prop="productCode">
          <el-input v-model="productForm.productCode" />
        </el-form-item>
        <el-form-item label="商品名称" prop="productName">
          <el-input v-model="productForm.productName" />
        </el-form-item>
        <el-form-item label="所属仓库" prop="warehouseId">
          <el-select
            v-model="productForm.warehouseId"
            placeholder="请选择仓库"
            clearable
            style="width: 100%"
          >
            <el-option
              v-for="warehouse in warehouseList"
              :key="warehouse.id"
              :label="warehouse.warehouseName"
              :value="warehouse.id"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="规格" prop="spec">
          <el-input v-model="productForm.spec" />
        </el-form-item>
        <el-form-item label="单位" prop="unit">
          <el-input v-model="productForm.unit" />
        </el-form-item>
        <el-form-item label="库存" prop="stock">
          <el-input-number v-model="productForm.stock" :precision="0" :min="0" style="width: 100%" />
        </el-form-item>
        <el-form-item label="分类" prop="category">
          <el-input v-model="productForm.category" />
        </el-form-item>
        <el-form-item label="成本价" prop="costPrice">
          <el-input-number v-model="productForm.costPrice" :precision="2" :min="0" style="width: 100%" />
        </el-form-item>
        <el-form-item label="采购价" prop="purchasePrice">
          <el-input-number v-model="productForm.purchasePrice" :precision="2" :min="0" style="width: 100%" />
        </el-form-item>
        <el-form-item label="销售价" prop="salePrice">
          <el-input-number v-model="productForm.salePrice" :precision="2" :min="0" style="width: 100%" />
        </el-form-item>
        <el-form-item label="状态" prop="status">
          <el-radio-group v-model="productForm.status">
            <el-radio :label="1">启用</el-radio>
            <el-radio :label="0">禁用</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="备注" prop="remark">
          <el-input v-model="productForm.remark" type="textarea" :rows="3" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleSubmit">确定</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, computed } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import {
  getProductList,
  getProductDetail,
  createProduct,
  updateProduct
} from '../api/internalProduct'
import { exportProductList } from '../api/internalProduct'
import { getWarehouseList } from '../api/internalWarehouse'

// 获取用户信息，判断是否为业务员（只读模式）
const getUserInfo = () => {
  try {
    const userInfoStr = localStorage.getItem('userInfo')
    if (userInfoStr) {
      return JSON.parse(userInfoStr)
    }
  } catch (e) {
    console.warn('解析用户信息失败:', e)
  }
  return null
}

const userInfo = getUserInfo()
const isReadOnly = computed(() => {
  // 业务员角色只能查询，不能编辑、删除、新增
  return userInfo?.userType === 'SALESMAN'
})

// 数据
const loading = ref(false)
const productList = ref([])
const total = ref(0)
const warehouseList = ref([])
const exportLoading = ref(false)
// 上次导出时间，用于限流
const lastExportTime = ref(0)
// 导出限流间隔时间（毫秒），3秒内不允许重复导出
const EXPORT_THROTTLE_INTERVAL = 3000
const queryParams = reactive({
  keyword: '',
  warehouseId: null,
  status: null,
  page: 1,
  pageSize: 10
})

// 对话框
const dialogVisible = ref(false)
const dialogTitle = ref('新增商品')
const isEdit = ref(false)
const productFormRef = ref(null)
const productForm = reactive({
  id: null,
  productCode: '',
  productName: '',
  warehouseId: null,
  spec: '',
  unit: '',
  stock: 0,
  category: '',
  costPrice: null,
  purchasePrice: null,
  salePrice: null,
  status: 1,
  remark: ''
})

const productFormRules = {
  productCode: [{ required: true, message: '请输入商品编码', trigger: 'blur' }],
  productName: [{ required: true, message: '请输入商品名称', trigger: 'blur' }],
  spec: [{ required: true, message: '请输入规格', trigger: 'blur' }],
  unit: [{ required: true, message: '请输入单位', trigger: 'blur' }]
}

// 方法
const loadWarehouseList = async () => {
  try {
    const res = await getWarehouseList({ page: 1, pageSize: 1000 })
    if (res.code === '000000') {
      warehouseList.value = res.data.list || []
    }
  } catch (error) {
    console.error('获取仓库列表失败:', error)
  }
}

const loadProductList = async () => {
  loading.value = true
  try {
    const params = { ...queryParams }
    const res = await getProductList(params)
    if (res.code === '000000') {
      productList.value = res.data.list || []
      total.value = res.data.total || 0
    }
  } catch (error) {
    console.error('获取商品列表失败:', error)
    ElMessage.error('获取商品列表失败')
  } finally {
    loading.value = false
  }
}

const handleSearch = () => {
  queryParams.page = 1
  loadProductList()
}

const handleReset = () => {
  queryParams.keyword = ''
  queryParams.warehouseId = null
  queryParams.status = null
  queryParams.page = 1
  loadProductList()
}

// 生成商品编码
const generateProductCode = () => {
  const now = new Date()
  const year = now.getFullYear().toString().slice(-2)
  const month = (now.getMonth() + 1).toString().padStart(2, '0')
  const day = now.getDate().toString().padStart(2, '0')
  const timestamp = now.getTime().toString().slice(-6)
  const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0')
  return `PRD${year}${month}${day}${timestamp}${random}`
}

const handleCreate = () => {
  dialogTitle.value = '新增商品'
  isEdit.value = false
  Object.assign(productForm, {
    id: null,
    productCode: generateProductCode(),
    productName: '',
    warehouseId: null,
    spec: '',
    unit: '',
    stock: 0,
    category: '',
    costPrice: null,
    purchasePrice: null,
    salePrice: null,
    status: 1,
    remark: ''
  })
  dialogVisible.value = true
}

const handleEdit = async (row) => {
  dialogTitle.value = '编辑商品'
  isEdit.value = true
  try {
    const res = await getProductDetail(row.id)
    if (res.code === '000000') {
      Object.assign(productForm, res.data)
      dialogVisible.value = true
    }
  } catch (error) {
    console.error('获取商品详情失败:', error)
    ElMessage.error('获取商品详情失败')
  }
}

const handleSubmit = async () => {
  if (!productFormRef.value) return
  await productFormRef.value.validate(async (valid) => {
    if (valid) {
      try {
        // 确保 warehouseId 正确传递（如果为空字符串，转换为 null）
        const submitData = { ...productForm }
        if (submitData.warehouseId === '' || submitData.warehouseId === undefined) {
          submitData.warehouseId = null
        }
        const res = isEdit.value 
          ? await updateProduct(submitData) 
          : await createProduct(submitData)
        if (res.code === '000000') {
          ElMessage.success(isEdit.value ? '更新成功' : '创建成功')
          dialogVisible.value = false
          loadProductList()
        }
      } catch (error) {
        console.error('保存商品失败:', error)
        const errorData = error.response?.data
        ElMessage.error(errorData?.msg || errorData?.message || '保存商品失败')
      }
    }
  })
}

const handleDialogClose = () => {
  productFormRef.value?.resetFields()
}

const handleToggleStatus = async (row) => {
  const newStatus = row.status === 1 ? 0 : 1
  const statusText = newStatus === 1 ? '启用' : '禁用'
  try {
    await ElMessageBox.confirm(`确定要${statusText}该商品吗？`, '提示', {
      type: 'warning',
      confirmButtonText: '确定',
      cancelButtonText: '取消'
    })
    const res = await updateProduct({
      ...row,
      status: newStatus
    })
    if (res.code === '000000') {
      ElMessage.success(`${statusText}成功`)
      loadProductList()
    }
  } catch (error) {
    if (error !== 'cancel') {
      console.error(`${statusText}商品失败:`, error)
      const errorData = error.response?.data
      ElMessage.error(errorData?.msg || errorData?.message || `${statusText}商品失败`)
    }
  }
}

const handleSizeChange = (size) => {
  queryParams.pageSize = size
  loadProductList()
}

const handlePageChange = (page) => {
  queryParams.page = page
  loadProductList()
}

// 导出商品列表
const handleExport = async () => {
  // 限流检查：如果正在导出，直接返回
  if (exportLoading.value) {
    return
  }

  // 限流检查：如果距离上次导出时间太短，提示用户
  const now = Date.now()
  const timeSinceLastExport = now - lastExportTime.value
  if (lastExportTime.value > 0 && timeSinceLastExport < EXPORT_THROTTLE_INTERVAL) {
    const remainingSeconds = Math.ceil((EXPORT_THROTTLE_INTERVAL - timeSinceLastExport) / 1000)
    ElMessage.warning(`导出操作过于频繁，请等待 ${remainingSeconds} 秒后再试`)
    return
  }
  
  try {
    exportLoading.value = true
    
    const exportParams = {
      keyword: queryParams.keyword,
      warehouseId: queryParams.warehouseId,
      status: queryParams.status
    }
    
    const blob = await exportProductList(exportParams)
    
    if (!blob || blob.size === 0) {
      throw new Error('导出失败：返回数据格式错误')
    }
    
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `商品列表_${new Date().toISOString().slice(0, 10).replace(/-/g, '')}_${Date.now()}.xlsx`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
    
    ElMessage.success('导出成功')
    // 导出成功后才记录时间戳，失败时不限流
    lastExportTime.value = Date.now()
  } catch (error) {
    console.error('导出失败:', error)
    const errorMsg = error.message || '导出失败'
    // 如果是限流错误（数据量过大），使用warning提示
    if (errorMsg.includes('数据量过大') || errorMsg.includes('超过最大限制')) {
      ElMessage.warning(errorMsg)
    } else {
      ElMessage.error(errorMsg)
    }
    // 导出失败时不更新时间戳，允许用户立即重试
  } finally {
    exportLoading.value = false
  }
}

// 生命周期
onMounted(() => {
  loadWarehouseList()
  loadProductList()
})
</script>

<style scoped>
.product-management {
  padding: 20px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.search-form {
  margin-bottom: 20px;
}

.pagination {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
}
</style>

